import React from "react";
import classNames from "classnames";
import { useHistory } from "react-router-dom";

import { ServiceProviderR } from "../../../pages/service-provider/routes";
import { ProcManagementR } from "../../../pages/proc-management/route";
import { getMaxLength } from "../../../../application/domain/proc-management/util/input-max-length";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import apiClient from "../../../../application/apis/api-client";
import {
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";

export const ProcedureCopyModal: React.FC<{
  orgId: string;
  name: string;
  isServiceProviderAccount: boolean;
  smartApply?: Pick<ProcedureEntity, "id" | "publishStatus" | "orgId">;
  onCopy: (name: string) => Promise<string | undefined>;
  onClose: () => void;
}> = ({
  orgId,
  name,
  isServiceProviderAccount = false,
  onCopy,
  onClose,
  smartApply,
}) => {
  const history = useHistory();
  const newProcNameSuffix = " コピー";
  const defaultProcName =
    name.length <= getMaxLength("procName") - newProcNameSuffix.length
      ? `${name}${newProcNameSuffix}`
      : name;
  const [newName, setNewName] = React.useState(defaultProcName);
  const [isInvalid, setIsInvalid] = React.useState(false);
  const handleCopy = React.useCallback(async () => {
    const newId = await onCopy(newName);
    if (typeof newId === "string") {
      onClose();
      isServiceProviderAccount
        ? history.push(
            ServiceProviderR.ProcManagementEdit.link({
              orgId: orgId,
              procId: newId,
            }),
          )
        : history.push(ProcManagementR.ProcedureEdit.link({ procId: newId }));
    }
  }, [
    isServiceProviderAccount,
    newName,
    history,
    ServiceProviderR,
    ProcManagementR,
    onClose,
    onCopy,
  ]);

  const smartApplyAssertion = useSmartApplyAssertion(smartApply?.id);

  return (
    <>
      <section className="modal-card-body">
        <div className="field">
          <div className="is-small">
            「{name}」をコピーして新規手続きを作成します。
          </div>
          {typeof smartApplyAssertion.error === "string" && (
            <p className="help is-danger">{smartApplyAssertion.error}</p>
          )}
        </div>
        <div className="field">
          <div className="label">手続き名称</div>
          <input
            className={classNames("input", {
              "is-danger": isInvalid,
            })}
            value={newName}
            onChange={(e) => {
              const name = e.target.value;
              setNewName(name);
              setIsInvalid(
                name.length === 0 || name.length > getMaxLength("procName"),
              );
            }}
          />
          {isInvalid && (
            <p className="help is-danger">
              {`手続き名称は1文字以上、${getMaxLength(
                "procName",
              )}文字以下の値を入力してください。`}
            </p>
          )}
        </div>
      </section>
      <footer className="modal-card-foot">
        <SecondaryButton text={"キャンセル"} onClick={onClose} />
        <PrimaryButton
          text={"作成する"}
          onClick={handleCopy}
          disabled={
            isInvalid ||
            smartApplyAssertion.isLoading ||
            typeof smartApplyAssertion.error === "string"
          }
        />
      </footer>
    </>
  );
};

// あくまで暫定対応。
// - Service へ関連ロジックを集約すべき
// - スマート申請情報以外をコピーするなど、素直なオプションを用意すべき
//   - そもそもページの描画時にスマート申請情報を取得できなかったケースを丁寧にケアすべき
const useSmartApplyAssertion = (
  smartApplyId?: string,
): { isLoading: boolean; error?: string } => {
  const [result, setResult] = React.useState<
    ReturnType<typeof useSmartApplyAssertion>
  >({ isLoading: true, error: undefined });

  React.useEffect(() => {
    if (typeof smartApplyId !== "string") {
      setResult({
        isLoading: false,
        error: undefined,
      });
      return;
    }

    apiClient
      .fetchProcedure(smartApplyId)
      .then(() => {
        setResult({
          isLoading: false,
          error: undefined,
        });
      })
      .catch(() => {
        setResult({
          isLoading: false,
          error:
            "手続きに紐づいているスマート申請を取得できないので、コピーを行えません。「スマート申請を利用する」チェックを外して、「現在の内容を保存」したのち、再度コピーを試みてください。",
        });
      });
  }, [smartApplyId, setResult]);

  return result;
};
